import {
  resolvePayPlanPeriodState,
} from "@components/PayPlanRowCard/payPlanRowCardUtils";
import { formatPayRangeAmount } from "@components/utils/formatPayRangeAmount";
import { archivedTag } from "@components/FairPay/payRangeBadges";
import { buildPayRangeVersionMenuItems } from "@components/PayRangeVersionRowCard/payRangeVersionMenus";
import {
  formatVersionAmountLabel,
  resolveVersionPeriodState,
} from "@components/PayRangeTimelineAndList/payRangeTimelineUtils";
import {
  WORK_MODE_LABEL,
  buildVersionHistory,
  resolveCardState,
  toNumber,
} from "./mapPayRangeDetailToCard";
import { formatFairPayDate } from "@components/utils/fairPayFormat";

const PAY_BASIS_LABEL = { HOURLY: "Hourly", MONTHLY: "Monthly", ANNUAL: "Annually" };

export const RETIRED_BANNER_TEXT =
  "This pay range is retired. Versions are read-only. Reactivate it to publish a new version.";
export const ARCHIVED_BANNER_TEXT =
  "This pay range is archived. Unarchive it to see it in the list and make changes.";

const HEADER_MENU = {
  edit: { name: "Edit", value: "edit", icon: "icons/dropdown/edit-05.svg" },
  publish: { name: "Publish", value: "publish", icon: "icons/dropdown/send-01.svg" },
  discard: {
    name: "Discard draft",
    value: "discard-draft",
    icon: "icons/dropdown/trash-01.svg",
    negative: true,
  },
  newVersion: {
    name: "New version",
    value: "new-version",
    icon: "icons/dropdown/file-plus-02.svg",
  },
  editDraft: { name: "Edit draft", value: "edit-draft", icon: "icons/dropdown/edit-05.svg" },
  publishDraft: {
    name: "Publish draft",
    value: "publish-draft",
    icon: "icons/dropdown/send-01.svg",
  },
  retire: { name: "Retire", value: "retire", icon: "icons/dropdown/activate.svg" },
  reactivate: { name: "Reactivate", value: "reactivate", icon: "icons/dropdown/activate.svg" },
  archive: { name: "Archive", value: "archive", icon: "icons/dropdown/archive-new.svg" },
  unarchive: { name: "Unarchive", value: "unarchive", icon: "icons/dropdown/unarchive.svg" },
};

/** Lifecycle actions of the pay range itself, keyed by the legacy card state. */
export function buildHeaderMenuItems(cardState) {
  switch (cardState) {
    case "first-draft":
      return [HEADER_MENU.edit, HEADER_MENU.publish, HEADER_MENU.discard];
    case "current":
    case "published-multi-no-draft":
      return [HEADER_MENU.newVersion, HEADER_MENU.retire];
    case "published-multi":
      return [
        HEADER_MENU.editDraft,
        HEADER_MENU.publishDraft,
        HEADER_MENU.discard,
        HEADER_MENU.retire,
      ];
    case "retired":
      return [HEADER_MENU.reactivate, HEADER_MENU.archive];
    case "archived":
      return [HEADER_MENU.unarchive];
    default:
      return [];
  }
}

/**
 * Rule 33: a DRAFT range has no version rows (Rule 19), so its staged amounts are
 * shown as its first version, Draft, with no number yet. Never published, so it
 * carries no version chip (Eric, 25-Oct-2026).
 */
function buildFirstDraftVersion(api) {
  const snap = api.compensation_snapshot || {};
  return {
    key: "v1-draft",
    versionNumber: null,
    status: "draft",
    startDate: api.draft_effective_from || null,
    endDate: null,
    minAmount: toNumber(api.draft_min_amount) ?? toNumber(snap.min_amount),
    maxAmount: toNumber(api.draft_max_amount) ?? toNumber(snap.max_amount),
    usageCount: 0,
  };
}

function buildDetailVersionRows(api, cardState) {
  const rows = buildVersionHistory(api);
  const isFirstDraft = String(api.status || "").toUpperCase() === "DRAFT";
  if (cardState === "first-draft" && isFirstDraft && !rows.length) {
    return [buildFirstDraftVersion(api)];
  }
  return rows;
}

/**
 * Version rows for the timeline and the list. A retired or archived range has no
 * current version: its last active version ends on the retire date. On an archived
 * range every non-draft version reads "retired".
 */
export function buildDetailVersions(api = {}) {
  const cardState = resolveCardState(api);
  const closed = cardState === "retired" || cardState === "archived";
  const hasHistory = Array.isArray(api.versions) && api.versions.length > 0;
  const source = cardState === "archived" && !hasHistory ? { ...api, status: "RETIRED" } : api;
  const firstDraftRange = cardState === "first-draft";
  const closedOn = api.retired_on || api.compensation_snapshot?.effective_to || null;

  return buildDetailVersionRows(source, cardState).map((row) => {
    let status = cardState === "archived" || row.status === "retired" ? "retired" : "in-force";
    if (row.status === "draft") status = "draft";
    else if (row.status === "voided") status = "voided";

    let effectiveTo = row.endDate || null;
    if (closed && status !== "draft" && status !== "voided" && !effectiveTo) effectiveTo = closedOn;

    return {
      id: row.key,
      versionId: row.versionId ?? null,
      versionNumber: row.versionNumber,
      status,
      effectiveFrom: row.startDate || null,
      effectiveTo,
      minAmount: row.minAmount,
      maxAmount: row.maxAmount,
      usageCount: row.usageCount || 0,
      corrected: Boolean(row.correctedAt),
      correctedAt: row.correctedAt || null,
      correctedByName: row.correctedByName || "",
      locked: (row.usageCount || 0) > 0,
      firstDraft: firstDraftRange && status === "draft",
    };
  });
}

function periodStateOf(version, today) {
  if (version.status === "draft") return "draft";
  return resolvePayPlanPeriodState(version.effectiveFrom, version.effectiveTo, today);
}

/** The version the header describes: draft, else current, else next future, else latest lapsed. */
export function pickDisplayedVersion(versions, today) {
  const live = versions
    .filter((version) => version.status !== "voided")
    .map((version) => ({ version, state: periodStateOf(version, today) }));
  const byNumber = (a, b) => b.version.versionNumber - a.version.versionNumber;

  const draft = live.filter((item) => item.state === "draft").sort(byNumber)[0];
  if (draft) return draft;

  const current = live.filter((item) => item.state === "current").sort(byNumber)[0];
  if (current) return current;

  const future = live
    .filter((item) => item.state === "future")
    .sort((a, b) => a.version.versionNumber - b.version.versionNumber)[0];
  if (future) return future;

  return live.sort(byNumber)[0] || null;
}

function formatEffectiveText(version) {
  if (!version) return "";
  const from = formatFairPayDate(version.effectiveFrom);
  if (!from) return "Effective date not set";
  const to = version.effectiveTo ? formatFairPayDate(version.effectiveTo) : "Open-ended";
  return `${from} – ${to}`;
}

function buildIdentityLine(api) {
  const level = api.job_family_level || {};
  const familyName = level.job_family?.name || "";
  const ordinal = level.ordinal != null && level.ordinal !== "" ? `Level ${level.ordinal}` : "";
  const levelName = level.name || "";
  const levelPart = [ordinal, levelName].filter(Boolean).join(" · ");
  const structured = [familyName, levelPart].filter(Boolean).join(" › ");
  return structured || level.label || "";
}

function buildLocationParts(api) {
  const region =
    api.location_region?.display_label ||
    api.location_region?.name ||
    String(api.operating_region || "").trim();
  const subRegion =
    api.location_metro_area?.display_label ||
    api.location_metro_area?.name ||
    api.location_local_unit?.display_label ||
    api.location_local_unit?.name ||
    "";
  return [region, subRegion || (region ? "" : "Country Wide")].filter(Boolean);
}

function buildIdentitySegments(api, currencyCode, payBasis) {
  const country = api.operating_country || {};
  const workMode =
    api.work_mode && String(api.work_mode).toUpperCase() !== "UNSPECIFIED"
      ? WORK_MODE_LABEL[api.work_mode] || api.work_mode
      : "";
  return [
    api.code || "",
    buildIdentityLine(api),
    `Paid in ${currencyCode}`,
    PAY_BASIS_LABEL[payBasis] || PAY_BASIS_LABEL.ANNUAL,
    workMode,
    country.name || country.code || "",
    ...buildLocationParts(api),
  ].filter(Boolean);
}

/** Readiness ladder 0% / 50% / 100% / 150% across the version's min…max, without a currency prefix. */
function buildReadinessPoints(version) {
  const lo = toNumber(version?.minAmount);
  const hi = toNumber(version?.maxAmount);
  if (lo == null || hi == null) return [];
  const span = Math.max(hi - lo, 0);
  return [
    { label: "0%", value: formatPayRangeAmount(lo) },
    { label: "50%", value: formatPayRangeAmount(lo + span / 3) },
    { label: "100%", value: formatPayRangeAmount(lo + (span * 2) / 3) },
    { label: "150%", value: formatPayRangeAmount(hi) },
  ];
}

/**
 * Header model for PayRangeDetailHeader and the version list for
 * PayRangeTimelineAndList, derived from the raw pay range detail payload.
 */
export function mapPayRangeDetailToView(api = {}, today = "") {
  const cardState = resolveCardState(api);
  const versions = buildDetailVersions(api);
  const displayed = pickDisplayedVersion(versions, today);
  const closed = cardState === "retired" || cardState === "archived";
  const currencyCode = api.operating_currency?.code || "USD";
  const payBasis = api.pay_basis || "ANNUAL";
  const displayedVersion = displayed?.version || null;
  const isDraft = displayed?.state === "draft";
  const periodState = closed && !isDraft ? "lapsed" : displayed?.state;
  const isFirstDraft = Boolean(isDraft && displayedVersion?.firstDraft);
  // Rule 33: a Draft's State comes from its dates and today, exactly as in its row.
  const bannerPeriodState = isDraft ? resolveVersionPeriodState(displayedVersion, today) : periodState;

  return {
    cardState,
    pageTone: closed ? cardState : "default",
    archivedTag: archivedTag(api),
    versionTone: periodState || "current",
    identitySegments: buildIdentitySegments(api, currencyCode, payBasis),
    amountLabel: displayedVersion ? formatVersionAmountLabel(displayedVersion, currencyCode) : "",
    periodText: formatEffectiveText(displayedVersion),
    readiness: buildReadinessPoints(displayedVersion),
    displayedVersion: displayedVersion
      ? {
          id: displayedVersion.id,
          versionNumber: displayedVersion.versionNumber,
          versionCode: isFirstDraft
            ? ""
            : `${api.code || ""}.${
                isDraft || displayedVersion.versionNumber == null
                  ? "?"
                  : `V${displayedVersion.versionNumber}`
              }`.replace(/^\./, ""),
          status: displayedVersion.status,
          firstDraft: isFirstDraft,
          periodState: bannerPeriodState,
          isDraft,
        }
      : null,
    banner:
      cardState === "archived"
        ? ARCHIVED_BANNER_TEXT
        : cardState === "retired"
        ? RETIRED_BANNER_TEXT
        : "",
    menuItems: buildHeaderMenuItems(cardState),
    versionMenuItems: displayedVersion
      ? buildPayRangeVersionMenuItems({
          status: displayedVersion.status,
          locked: displayedVersion.locked,
          readOnly: closed,
        })
      : [],
    payRange: {
      id: api.id,
      code: api.code || "",
      currencyCode,
      versions,
    },
    readOnly: closed,
  };
}
